const HomeProcess = () => {
  return (
    <section className="py-32 px-6 relative overflow-hidden bg-gradient-to-br from-white via-slate-50 to-gray-100 dark:from-gray-800 dark:via-gray-900 dark:to-black">
      {/* Sophisticated background texture */}
      <div className="absolute inset-0 opacity-20 dark:opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-pink-200/30 via-transparent to-transparent" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-radial from-purple-200/30 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Elegant heading */}
        <div className="text-center mb-24">
          <p className="text-xs font-light tracking-[0.4em] uppercase text-gray-400 dark:text-gray-500 mb-6">
            The Atelier Process
          </p>
          <h2 className="text-5xl md:text-6xl font-thin mb-8 text-gray-900 dark:text-white leading-tight">
            Reimagining Your
            <br />
            <span className="italic font-extralight bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Existing Collection
            </span>
          </h2>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent mx-auto" />
        </div>

        {/* Sophisticated process steps */}
        <div className="grid md:grid-cols-3 gap-16">
          {[
            {
              phase: "Rediscovery",
              title: "Wardrobe Renaissance",
              description:
                "Uncover the hidden potential in every piece. We catalog your wardrobe with precision—materials, silhouettes, and color stories—revealing forgotten gems and mapping where each item shines.",
              icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
              accent: "from-slate-300 to-gray-400",
              number: "01",
            },
            {
              phase: "Reinvention",
              title: "Creative Resurrection",
              description:
                "Watch neglected pieces become statement looks. The outfit generator assembles event‑ready ensembles from what you already own, optimized for season, context, and your personal narrative.",
              icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              ),
              accent: "from-rose-300 to-pink-400",
              number: "02",
            },
            {
              phase: "Renewal",
              title: "Sustainable Sophistication",
              description:
                "Embrace conscious luxury through intentional rewear. Rotate pieces, build capsules, and elevate cost‑per‑wear—all while discovering refined new ways to express your evolving style.",
              icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              ),
              accent: "from-purple-300 to-indigo-400",
              number: "03",
            },
          ].map(({ phase, title, description, icon, accent, number }, i) => (
            <div key={i} className="group relative">
              {i < 2 && (
                <div className="hidden md:block absolute top-16 left-full w-16 h-px bg-gradient-to-r from-gray-300 to-transparent dark:from-gray-600 z-0" />
              )}

              <div className="relative bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm border border-gray-200/30 dark:border-gray-700/30 p-10 transition-all duration-700 group-hover:bg-white/80 dark:group-hover:bg-gray-900/80 group-hover:shadow-xl group-hover:shadow-gray-200/50 dark:group-hover:shadow-gray-800/50">
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                  <span className="text-xs font-light tracking-wider text-gray-400 dark:text-gray-500">{number}</span>
                </div>

                <div className={`text-gray-400 dark:text-gray-500 mb-8 group-hover:text-transparent group-hover:bg-gradient-to-br group-hover:${accent} group-hover:bg-clip-text transition-all duration-500`}>
                  {icon}
                </div>

                <p className="text-xs font-light tracking-[0.3em] uppercase text-gray-400 dark:text-gray-500 mb-3">{phase}</p>
                <h3 className="text-xl font-light mb-6 text-gray-900 dark:text-white tracking-wide leading-tight">{title}</h3>
                <p className="text-sm font-light text-gray-600 dark:text-gray-300 leading-relaxed tracking-wide">{description}</p>
                <div className={`w-0 group-hover:w-16 h-px bg-gradient-to-r ${accent} mt-8 transition-all duration-700`} />
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center items-center mt-20 space-x-8">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 border border-gray-300 dark:border-gray-600 rotate-45" />
            <div className="w-12 h-px bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500" />
            <div className="w-3 h-3 border border-gray-300 dark:border-gray-600 rotate-45" />
            <div className="w-12 h-px bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500" />
            <div className="w-3 h-3 border border-gray-300 dark:border-gray-600 rotate-45" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeProcess;
